/**
 * Login page handling user authentication, new account registration,
 * and the forgot-password flow, with branding asset fetching.
 *
 * @module pages/login/Login
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useState, useEffect} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    InputLabel,
    Paper,
    TextField,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import {connect} from 'react-redux';
import {Redirect, useLocation} from 'react-router-dom';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
import {
    fetchNotification,
    fetchPermissionDetails,
    loginUser,
    registerUser,
    updateLoginStatus,
} from '../../redux/authentication/action';
import {fetchBranding, forgotPassword} from '../../services/result.service';
import loginBg from '../../assets/images/login/image_placeholder.png';
import iLearnLogo from '../../assets/images/login/logo_placeholder.gif';
import LoginStyle from './Login';
import {clearAudioStorage} from "../../utils/audioFetch";
import useAlert from "../../components/common/UseAlert";
import AlertPopup from "../../components/common/AlertPopup.jsx";

/**
 * Represents the Login component in the application, which provides user authentication
 * and account creation functionalities.
 *
 * This component is responsible for:
 * - Rendering the login form with email and password fields.
 * - Authenticating existing users using the `loginUser` function.
 * - Handling user registration with the `registerUser` function.
 * - Managing multiple states, including authentication status, alerts, registration forms,
 *   and branding assets.
 * - Fetching permission details and notifications for authenticated users.
 * - Displaying relevant alerts for login or registration errors and successes.
 *
 * **Props**:
 * @param {Function} loginUser - Function to authenticate a user with email and password.
 * @param {boolean} isAuth - Indicates if the user is currently authenticated.
 * @param {Function} fetchPermissionDetails - Function to fetch permission details for the
 *     authenticated user.
 * @param {string} errMessage - Error message related to login attempts.
 * @param {boolean | null} loginStatus - Defines the current status of login attempts.
 * @param {Function} updateLoginStatus - Updates the current login state.
 * @param {boolean | null} registrationStatus - Tracks if the registration was successful or not.
 * @param {string} registrationMessage - Message related to the registration process.
 * @param {Function} registerUser - Function to initiate user registration.
 * @param {Function} fetchNotification - Function to fetch notifications for the logged-in user.
 *
 * The component also leverages internal state to manage the following:
 * - Validation of user inputs (email and password) during login and registration.
 * - Displaying alerts for errors or successful operations.
 * - Handling UI interactions, such as showing/hiding the password and toggling components.
 * - Loading states during asynchronous operations like registration and branding fetching.
 */
const Login = ({
    loginUser,
    isAuth,
    fetchPermissionDetails,
    errMessage,
    loginStatus,
    updateLoginStatus,
    registrationStatus,
    registrationMessage,
    registerUser,
    fetchNotification,
}) => {
    const useStyles = makeStyles((theme) => LoginStyle(theme));
    const classes = useStyles();
    const {setAlert} = useAlert();

    const branding = React.useRef(null);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [openRegistration, setOpenRegistration] = useState(false);
    const [flag, setFlag] = React.useState(false);
    const [registrationForm, setRegistrationForm] = useState({
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        password: '',
        company_name: '',
        showPassword: false,
    });
    const [loading, setLoading] = useState(false);
    const [registerFlag, setRegisterFlag] = useState(false);
    const [registerSuccess, setRegisterSuccess] = useState(true);
    const [brandingLogo, setBrandingLogo] = React.useState(null);
    const [brandingSplash, setBrandingSplash] = React.useState(null);
    const [allowNewCompany, setAllowNewCompany] = useState(false);

    const rlcKey = 'XAB1QxEF7Wu/NRAG4';
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const allowNewCompanyParam = urlParams.get("allowNewCompany");

    useEffect(
        () => {
            console.log('Login useEffect [isAuth, fetchNotification]');
            if (isAuth && !localStorage.getItem('ntf')) {
                fetchNotification(0, false);
            }
        },
        [isAuth, fetchNotification]
    );

    useEffect(
        () => {
            let isMounted = true;

            canAddCompany().then((result) => {
                if (isMounted) setAllowNewCompany(result);

                clearAudioStorage().then(() => {
                    if (branding.current) {
                        return;
                    }

                    fetchBranding().then((result) => {
                        if (!isMounted) return;
                        if (result && result.success && result.brandingLogo) {
                            setBrandingLogo(result.brandingLogo);
                        }
                        if (result && result.success && result.brandingSplash) {
                            setBrandingSplash(result.brandingSplash);
                        }

                        branding.current = {...result};
                    });
                });
            });

            return () => { isMounted = false; };
        },
        []
    );

    useEffect(() => {
        if (!isAuth && (loginStatus === false) && !flag) {
            setFlag(true);
            updateLoginStatus(null);
            setAlert(errMessage, 'error');
        }
    }, [isAuth, loginStatus, flag, errMessage]);

    useEffect(() => {
        if (!isAuth && registrationStatus === false && !registerFlag) {
            setRegisterFlag(true);
            setLoading(false);
            updateLoginStatus(null);

            setAlert(registrationMessage, 'error');
        }
    }, [isAuth, registrationStatus, registerFlag, registrationMessage]);

    useEffect(() => {
        if (!isAuth && registrationStatus === true && registerSuccess) {
            setRegisterSuccess(false);
            setLoading(false);
            updateLoginStatus(null);
            setAlert(
                'Success!! Account is created. Please login with the credentials created.',
                'success'
            );

            const timer = setTimeout(() => {
                setOpenRegistration(false);
                setRegistrationForm({
                    first_name: '',
                    last_name: '',
                    phone_number: '',
                    email: '',
                    password: '',
                    company_name: '',
                    showPassword: false,
                });
            }, 5000);

            return () => clearTimeout(timer);
        }
    }, [isAuth, registrationStatus, registerSuccess]);

    /**
     * Verifies whether the URL contains a valid signed token that permits
     * new company self-registration on this login page.
     *
     * @async
     * @returns {Promise<boolean>} True if the token is valid and new companies are allowed.
     */
    const canAddCompany = async () => {
        let result = false;

        if (
            allowNewCompanyParam &&
            allowNewCompanyParam.length &&
            rlcKey &&
            rlcKey.length
        ) {
            const enc = new TextEncoder();
            const algorithm = {name: "HMAC", hash: "SHA-256"};

            // Import the key
            const key = await window.crypto.subtle.importKey(
                "raw",
                enc.encode(rlcKey),
                algorithm,
                false,
                ["sign"]
            );

            // Sign the data
            const signature = await window.crypto.subtle.sign(
                algorithm.name,
                key,
                enc.encode(allowNewCompanyParam)
            );

            // Convert the signature to a base64 string (optional)
            const digest = btoa(String.fromCharCode(...new Uint8Array(signature)));
            result = (digest === 'VFaBnk6/M8WwPi+BKe1DqebLthY=') ||
                (digest === 'NET/Y1cFBJdrtk3LlIV6nJ2/6GFiCYl7o4DE0mCrDQk=');
        }

        return result;
    };

    /**
     * Asynchronous function to handle form submission.
     *
     * This function is intended to manage the login form submission,
     * including validation of email and password, and initiating the login process.
     *
     * @param {Object} e - The event object triggered by the form submission.
     *
     * Performs the following:
     *  - Prevents the default form submission behavior.
     *  - Resets specific state flags related to the registration process.
     *  - Validates the email field against a standard email pattern or a unique ID pattern.
     *    If invalid, an alert is displayed.
     *  - Alerts the user if the email or password fields are empty.
     *  - If valid, constructs a payload object containing email and password
     *    and calls the `loginUser` function to process login.
     */
    const submitForm = async (e) => {
        setFlag(false);
        setRegisterSuccess(true);
        setRegisterFlag(false);
        e.preventDefault();

        if (email.trim() !== '' && password.trim() !== '') {
            const emailPattern = new RegExp(
                    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i, //eslint-disable-line
                ),
                uniqueIdPattern = new RegExp(/^[0-9,a-f]{24}$/i);

            if (!emailPattern.test(email) && !uniqueIdPattern.test(email)) {
                setAlert('Please enter valid email address!!', 'error');
            } else {
                const payload = {
                    email,
                    password,
                };
                try {
                    await loginUser(payload);
                } catch (error) {
                    setAlert(error.message, 'error');
                }

            }
        } else {
            setAlert('Please enter your email and password', 'error');
        }
    }

    /**
     * Closes the registration form modal and resets the form fields to their default values.
     *
     * This function performs the following actions:
     * - Sets the `openRegistration` state to `false` to close the registration modal.
     * - Resets the `registrationForm` state with default empty values for all form fields
     *   and sets `showPassword` to `false`.
     */
    const handleClose = () => {
        setOpenRegistration(false);
        setRegistrationForm({
            ...registrationForm,
            first_name: '',
            last_name: '',
            phone_number: '',
            email: '',
            password: '',
            company_name: '',
            showPassword: false,
        });
    };

    /**
     * Handles the account creation process based on user input.
     *
     * During this process:
     * - The registration flags are reset to ensure proper UI states.
     * - The input fields for email and password are checked to ensure they are non-empty.
     * - The email is validated against a regex pattern to confirm it is in a valid format.
     * - If validation succeeds, a payload containing user details is prepared and sent to the user
     * registration process.
     * - Feedback is provided to the user via alerts and loading indicators.
     *
     * @param {Object} e - The event object from the form submission.
     */
    const createAccount = (e) => {
        setRegisterFlag(false);
        setRegisterSuccess(true);
        e.preventDefault();
        if (
            (registrationForm.email.trim() !== '') &&
            (registrationForm.password.trim() !== '')
        ) {
            const pattern = new RegExp(
                /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i, //eslint-disable-line
            );
            if (!pattern.test(registrationForm.email)) {
                setAlert('Please enter valid email address!!', 'error');
            } else {
                setLoading(true);
                const payload = {
                    email: registrationForm.email,
                    password: registrationForm.password,
                    first_name: registrationForm.first_name,
                    last_name: registrationForm.last_name,
                    company_name: registrationForm.company_name,
                    phone_number: registrationForm.phone_number,
                };
                registerUser(payload);
                setAlert('Registration successful!', 'success');
            }
        }
    }

    /**
     * Updates the registration form state with the value from a specified input field.
     *
     * @param {string} prop - The key in the registration form object to be updated.
     * @returns {Function} A function that takes an event object and updates the corresponding
     *                     property in the registration form state with the input value.
     */
    const handleChange = (prop) => (event) => {
        setRegistrationForm({...registrationForm, [prop]: event.target.value});
    };

    /**
     * Toggles the visibility state of the password input field in the registration form.
     * Updates the `registrationForm` state by inverting the `showPassword` property.
     *
     * This function is typically used to allow users to toggle between hiding and showing
     * the password text in a form field for better usability and validation.
     */
    const handleClickShowPassword = () => {
        setRegistrationForm({...registrationForm, showPassword: !registrationForm.showPassword});
    };

    /**
     * Handles the "mousedown" event for the password input element.
     * Prevents the default action of the event to avoid unwanted
     * behavior such as focusing on the element when interacting
     * with password visibility toggle.
     *
     * @param {MouseEvent} event - The mouse event object triggered on mousedown.
     */
    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    /**
     * Closes the registration process by resetting the registration form fields to their initial
     * empty values and updating the state to indicate that the registration form is no longer
     * open.
     *
     * The following fields in the registration form are reset:
     * - first_name
     * - last_name
     * - phone_number
     * - email
     * - password
     * - company_name
     * - showPassword (set to false)
     *
     * Additionally, the open registration state is set to false to close the registration form.
     */
    const closeRegistration = () => {
        setRegistrationForm({
            ...registrationForm,
            first_name: '',
            last_name: '',
            phone_number: '',
            email: '',
            password: '',
            company_name: '',
            showPassword: false,
        });
        setOpenRegistration(false);
    };

    /**
     * Handles the forgot password functionality by validating the input email
     * and sending a password reset request if the email is valid.
     *
     * The function performs the following steps:
     * - Checks if the email input is not empty.
     * - Validates the email format using a regular expression.
     * - If the email is invalid, informs the user via an alert message.
     * - If the email is valid, it triggers the forgot password process and displays
     *   an alert indicating a password reset link has been sent.
     *
     * Preconditions:
     * - The `email` variable should be defined in the enclosing scope.
     *
     * Side Effects:
     * - Displays alerts to inform the user of validation errors or success messages.
     * - Calls the `forgotPassword` function to send a reset request.
     * - Updates the state to show a success alert via `setOpenAlert`, `setAlertMessage`,
     *   and `setSeverity`.
     */
    const forgotPass = () => {
        if (email.trim() !== '') {
            const pattern = new RegExp(
                /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i, //eslint-disable-line
            );
            if (!pattern.test(email)) {
                setAlert('Please enter valid email address!!', 'error');
            } else {
                forgotPassword({email, url: window.location.origin});
                setAlert('Please check your email for the password reset link', 'success');
            }
        } else {
            setAlert('Please enter your email', 'error');
        }
    };

    if (isAuth) {
        fetchPermissionDetails();
        fetchNotification(0, false);
        return (
            <Redirect to={localStorage.getItem('redirectUrl') || '/dashboard'}/>
        );
    }

    /**
     * Conditionally renders the "Create Account" link when new company
     * registration is permitted by the URL token.
     *
     * @returns {JSX.Element|null} The registration prompt, or null.
     */
    const renderAddCompanyForm = () => {
        if (allowNewCompany) {
            return (
                <Box align="center">
                    <Typography variant="caption">
                        No account yet?</Typography>
                    <Typography variant="subtitle1">
                        <Button color="primary"
                                onClick={() => setOpenRegistration(true)}>
                            <Typography
                                className={classes.boldFont}
                                variant="caption"
                            >
                                CREATE NOW
                            </Typography>
                        </Button>
                    </Typography>
                </Box>
            );
        }

        return null;
    }

    return (
        <div className={classes.root}>
            <AlertPopup />
            <Dialog open={openRegistration} fullWidth onClose={handleClose}>
                <div>
                    <IconButton onClick={handleClose} style={{float: 'right'}}>
                        <CloseIcon
                            style={{
                                transform: 'scale(4)',
                                backgroundColor: '#E9E9E9',
                                color: '#B1B1B1',
                                margin: '7px 5px 0px 0px',
                                width: '4px',
                                height: '4px',
                                padding: 1,
                                borderRadius: '1px',
                            }}
                        />
                    </IconButton>
                </div>
                <DialogTitle style={{textAlign: 'center'}}><strong>Create
                    Account</strong></DialogTitle>
                <DialogContent style={{padding: '0px 56px'}}>
                    <form className={classes.formControl} onSubmit={createAccount}>
                        <div style={{display: 'flex', justifyContent: 'space-between'}}>
                            <div>
                                <InputLabel className={classes.label}>
                                    First Name
                                </InputLabel>
                                <TextField
                                    className={classes.inputFieldPadding}
                                    required
                                    type="text"
                                    placeholder="First Name"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    InputProps={{
                                        classes: {
                                            root: classes.cssOutlinedInput,
                                            focused: classes.cssFocused,
                                            notchedOutline: classes.notchedOutline,
                                        },
                                        inputMode: 'string',
                                    }}
                                    variant="filled"
                                    size="small"
                                    value={registrationForm.first_name}
                                    onChange={handleChange('first_name')}
                                />
                            </div>
                            <div>
                                <InputLabel className={classes.label}>
                                    Last Name
                                </InputLabel>
                                <TextField
                                    className={classes.inputFieldPadding}
                                    required
                                    type="text"
                                    placeholder="Last Name"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    InputProps={{
                                        classes: {
                                            root: classes.cssOutlinedInput,
                                            focused: classes.cssFocused,
                                            notchedOutline: classes.notchedOutline,
                                        },
                                        inputMode: 'string',
                                    }}
                                    variant="filled"
                                    size="small"
                                    value={registrationForm.last_name}
                                    onChange={handleChange('last_name')}
                                />
                            </div>
                        </div>
                        <div>
                            <InputLabel className={classes.label}>
                                Company Name
                            </InputLabel>
                            <TextField
                                className={classes.inputFieldPadding}
                                required
                                type="text"
                                placeholder="Company Name"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                InputProps={{
                                    classes: {
                                        root: classes.cssOutlinedInput,
                                        focused: classes.cssFocused,
                                        notchedOutline: classes.notchedOutline,
                                    },
                                    inputMode: 'string',
                                }}
                                variant="filled"
                                size="small"
                                fullWidth
                                value={registrationForm.company_name}
                                onChange={handleChange('company_name')}
                            />
                        </div>
                        <div className={classes.loader}>
                            {loading ? (
                                <CircularProgress color="primary" className={classes.loaderIcon}/>
                            ) : null}
                        </div>
                        <div>
                            <InputLabel className={classes.label}>
                                Phone number
                            </InputLabel>
                            <NumberFormat
                                placeholder="Phone number"
                                onChange={handleChange('phone_number')}
                                customInput={TextField}
                                value={registrationForm.phone_number}
                                required
                                className={classes.numFormat}
                                format="+1 (###) ###-####"
                                mask="_"
                            />
                        </div>
                        <div>
                            <InputLabel className={classes.label}>
                                Email Address
                            </InputLabel>
                            <TextField
                                className={classes.inputFieldPadding}
                                required
                                type="email"
                                placeholder="Email address"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                InputProps={{
                                    classes: {
                                        root: classes.cssOutlinedInput,
                                        focused: classes.cssFocused,
                                        notchedOutline: classes.notchedOutline,
                                    },
                                    inputMode: 'string',
                                }}
                                variant="filled"
                                size="small"
                                fullWidth
                                value={registrationForm.email}
                                onChange={handleChange('email')}
                            />
                        </div>
                        <div>
                            <InputLabel className={classes.label}>Password</InputLabel>
                            <TextField
                                className={classes.passwordField}
                                required
                                id="input-with-icon-textfield"
                                type={registrationForm.showPassword ? 'text' : 'password'}
                                value={registrationForm.password}
                                onChange={handleChange('password')}
                                placeholder="Password"
                                variant="filled"
                                size="small"
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                onMouseDown={handleMouseDownPassword}
                                            >
                                                {registrationForm.showPassword
                                                    ?
                                                    <Visibility className={classes.passwordIcon}/>
                                                    :
                                                    <VisibilityOff
                                                        className={classes.passwordIcon}/>}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </div>
                        <Box mt={3} style={{textAlign: 'center', marginBottom: '12px'}}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disableElevation
                                size="small"
                                classes={{root: classes.button}}
                            >
                                CREATE ACCOUNT
                            </Button>
                        </Box>
                        <Box align="center" mb={5}>
                            <Typography variant="caption">Already have an account?</Typography>
                            <Typography variant="subtitle1">
                                <Button color="primary" onClick={closeRegistration}>
                                    <Typography
                                        className={classes.boldFont}
                                        variant="caption"
                                    >
                                        SIGN IN
                                    </Typography>
                                </Button>
                            </Typography>
                        </Box>
                    </form>
                </DialogContent>
            </Dialog>
            {/* Top bar */}
            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                {/* Logo */}
                <Grid item xs={6}>
                    <Box component="div" ml={5}>
                        {brandingLogo ? (
                            <img alt="logo" src={`${brandingLogo}`} width="85vw"/>
                        ) : (
                            <img alt="logo" src={iLearnLogo} width="85vw"/>
                        )
                        }
                    </Box>
                </Grid>
                <Grid item xs={3}>
                    <Box
                        container="true"
                        component="div"
                        borderRadius="borderRadius"
                        className={classes.navContainer}
                        mr={5}
                        fontWeight="fontWeightLight"
                    >
                        {/* <Button className={classes.linkFont} size="small">
                         CONTACT US
                         </Button>
                         <span>|</span>
                         <Button className={classes.linkFont} size="small">
                         ABOUT US
                         </Button>
                         <span>|</span>
                         <Button className={classes.linkFont} size="small">
                         HELP
                         </Button> */}
                    </Box>
                </Grid>
            </Grid>
            {/* Hero image and Login form */}
            <Grid container>
                <Grid item xs={2}/>
                <Grid item xs={8}>
                    <Box mt={5}>
                        <Grid className={classes.ligthBg} container>
                            {/* Hero Image */}
                            <Grid item xs={7}>
                                <Box component="div" mt={8}>
                                    {brandingSplash ? (
                                        <img alt="logo" src={`${brandingSplash}`} width="95%"/>
                                    ) : (
                                        <img alt="logo" src={loginBg} width="90%"/>
                                    )}
                                </Box>
                            </Grid>
                            {/* Login Form */}
                            <Grid item xs={5}>
                                <Box component="div">
                                    <Paper
                                        className={`${classes.root} ${classes.loginCardContainer}`}
                                        elevation={0}
                                    >
                                        <Typography
                                            variant="subtitle1"
                                            align="center"
                                            className={classes.boldFont}
                                        >
                                            Please Sign in!
                                        </Typography>
                                        <form className={classes.formControl} onSubmit={submitForm}>
                                            <InputLabel className={classes.label}>
                                                Email Id
                                            </InputLabel>
                                            <TextField
                                                id="email-input"
                                                className={classes.loginInputFieldPadding}
                                                style={{width: '100% !important'}}
                                                required
                                                type="text"
                                                placeholder="Enter registered email ID (Or a valid User ID)"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                InputProps={{
                                                    classes: {
                                                        root: classes.cssOutlinedInput,
                                                        focused: classes.cssFocused,
                                                        notchedOutline: classes.notchedOutline,
                                                    },
                                                    inputMode: 'string',
                                                }}
                                                variant="filled"
                                                size="small"
                                                fullWidth
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                            />
                                            <InputLabel className={classes.label}>
                                                Password
                                            </InputLabel>
                                            <TextField
                                                className={classes.loginInputFieldPadding}
                                                id="password-input"
                                                required
                                                type="password"
                                                placeholder="Enter password"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                InputProps={{
                                                    classes: {
                                                        root: classes.cssOutlinedInput,
                                                        focused: classes.cssFocused,
                                                        notchedOutline: classes.notchedOutline,
                                                    },
                                                    inputMode: 'string',
                                                }}
                                                variant="filled"
                                                size="small"
                                                fullWidth
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}

                                            />
                                            <Box mt={5}>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    fullWidth
                                                    color="primary"
                                                    disableElevation
                                                >
                                                    SIGN IN
                                                </Button>
                                            </Box>
                                            <Box className={classes.checkBoxContainer}>
                                                {/* <FormControlLabel
                                                 className={classes.smallFont}
                                                 control={(
                                                 <Checkbox
                                                 className={classes.smallFont}
                                                 checked={checked}
                                                 onChange={handleCheckBox}
                                                 name="remember"
                                                 id="rememberMe"
                                                 color="primary"
                                                 />
                                                 )}
                                                 label="REMEMBER ME"
                                                 /> */}
                                                <div color="primary" className={classes.btnDiv}>
                                                    <Typography variant="caption"
                                                                style={{whiteSpace: 'noWrap'}}>
                                                        <Button
                                                            className={classes.boldFont}
                                                            onClick={forgotPass}
                                                        >
                                                            FORGOT PASSWORD
                                                        </Button>
                                                    </Typography>
                                                </div>
                                            </Box>
                                            <Box my={3}>
                                                <Typography
                                                    // className={classes.borderLine}
                                                    type="title"
                                                    color="inherit"
                                                />
                                            </Box>
                                        </form>

                                        {renderAddCompanyForm()}

                                    </Paper>
                                </Box>
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>
            </Grid>
        </div>
    );
};

Login.propTypes = {
    isAuth: PropTypes.bool,
    loginUser: PropTypes.func,
    fetchPermissionDetails: PropTypes.func,
    errMessage: PropTypes.string,
    loginStatus: PropTypes.any,
    updateLoginStatus: PropTypes.func,
    registerUser: PropTypes.func,
    registrationMessage: PropTypes.string,
    registrationStatus: PropTypes.any,
    fetchNotification: PropTypes.func,
};

/**
 * Maps Redux state to the Login component props.
 *
 * @param {Object} state - The Redux store state.
 * @returns {Object} Props derived from state.
 */
const mapStateToProps = (state) => ({
    isAuth: state.login.isAuth,
    errMessage: state.login.errMessage,
    loginStatus: state.login.loginStatus,
    registrationMessage: state.login.registrationMessage,
    registrationStatus: state.login.registrationStatus,
});

/**
 * Maps Redux dispatch to the Login component props.
 *
 * @type {Object}
 */
const mapDispatchToProps = {
    loginUser,
    fetchPermissionDetails,
    updateLoginStatus,
    registerUser,
    fetchNotification,
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
